import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";

const TermsOfService = () => {
  return (
    <>
      <SEO 
        title="Terms of Service"
        description="Read the terms and conditions for using sksenq.com. These terms govern your access to our software engineering and cloud consulting services website."
        canonical="/terms-of-service"
      />
      <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-8 gap-2">
            <ArrowLeft size={16} />
            Back to Home
          </Button>
        </Link>

        <article className="prose prose-lg dark:prose-invert max-w-none">
          <h1 className="text-4xl font-bold mb-2">Terms of Service</h1>
          <p className="text-muted-foreground mb-8">Last updated: 28 December 2025</p>

          <p>
            These Terms of Service ("Terms") govern your access to and use of the website https://sksenq.com (the "Website") operated by Sksenq LLC ("we", "us", or "our").
          </p>
          <p>
            By accessing or using the Website, you agree to be bound by these Terms. If you do not agree, please do not use the Website.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">1. Company Information</h2>
          <ul className="list-none pl-0 space-y-1">
            <li><strong>Legal entity:</strong> Sksenq LLC</li>
            <li><strong>Country of registration:</strong> Armenia</li>
            <li><strong>Website:</strong> <a href="https://sksenq.com" className="text-primary hover:underline">https://sksenq.com</a></li>
            <li><strong>Contact email:</strong> <a href="mailto:contact@sksenq.com" className="text-primary hover:underline">contact@sksenq.com</a></li>
          </ul>
          <p>Sksenq LLC provides software engineering and cloud consulting services worldwide.</p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">2. Website Purpose</h2>
          <p>
            The Website is provided for informational purposes and to allow potential clients to contact us regarding software engineering, cloud consulting, and related professional services.
          </p>
          <p>The Website does not currently provide:</p>
          <ul>
            <li>User accounts</li>
            <li>Online payments or subscriptions</li>
            <li>Automated service delivery</li>
          </ul>
          <p>Submitting a contact request does not create a contractual relationship.</p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">3. Contact Requests</h2>
          <p>By submitting a request through our contact form, you agree that:</p>
          <ul>
            <li>The information you provide is accurate and truthful</li>
            <li>You are authorized to use the provided work email address</li>
            <li>We may contact you in response to your inquiry</li>
          </ul>
          <p>We reserve the right to reject or ignore inquiries that appear abusive, fraudulent, or irrelevant.</p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">4. No Guarantee of Services</h2>
          <p>Submitting a request or communicating with us does not guarantee:</p>
          <ul>
            <li>Acceptance of your project</li>
            <li>Availability of services</li>
            <li>Any specific response time or outcome</li>
          </ul>
          <p>Any engagement, pricing, or scope of work will be agreed upon separately in writing.</p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">5. Intellectual Property</h2>
          <p>
            All content on the Website, including but not limited to text, logos, branding, and design, is the intellectual property of Sksenq LLC unless otherwise stated.
          </p>
          <p>You may not copy, reproduce, distribute, or create derivative works without prior written permission.</p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">6. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Website for unlawful purposes</li>
            <li>Attempt to gain unauthorized access to systems or data</li>
            <li>Interfere with the operation or security of the Website</li>
            <li>Submit false or misleading information</li>
          </ul>
          <p>We may restrict or block access to the Website if these Terms are violated.</p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">7. Third-Party Services</h2>
          <p>
            The Website may rely on third-party infrastructure or services to operate (e.g., hosting, form handling, email delivery).
          </p>
          <p>We are not responsible for the content, availability, or practices of third-party services.</p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">8. Disclaimer</h2>
          <p>The Website and its content are provided on an "as is" and "as available" basis.</p>
          <p>We make no warranties, express or implied, regarding:</p>
          <ul>
            <li>Accuracy or completeness of content</li>
            <li>Availability or reliability of the Website</li>
            <li>Fitness for a particular purpose</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-10 mb-4">9. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, Sksenq LLC shall not be liable for any indirect, incidental, consequential, or special damages arising out of or related to your use of the Website.
          </p>
          <p>
            Our total liability shall not exceed the amount, if any, paid by you to us directly for services agreed under a separate contract.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">10. Indemnification</h2>
          <p>You agree to indemnify and hold harmless Sksenq LLC from any claims, damages, losses, or expenses arising from:</p>
          <ul>
            <li>Your misuse of the Website</li>
            <li>Your violation of these Terms</li>
            <li>Your submission of unlawful or infringing content</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-10 mb-4">11. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the Republic of Armenia, without regard to conflict of law principles.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">12. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. Updated versions will be posted on this page with a revised "Last updated" date.
          </p>
          <p>Continued use of the Website after changes constitutes acceptance of the updated Terms.</p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">13. Contact Information</h2>
          <p>If you have questions about these Terms, please contact:</p>
          <p>
            <a href="mailto:contact@sksenq.com" className="text-primary hover:underline">contact@sksenq.com</a>
          </p>
        </article>
      </div>
      </div>
    </>
  );
};

export default TermsOfService;
